import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { alpha } from '@mui/material/styles';
import { tokens, UI_FONT } from '../../../theme';
import { formatMoney } from '../../../utils/format';
import { CHART_COLORS, formatNumber } from './statsShared';

const AXIS_TICK = { fontSize: 12, fill: tokens.muted, fontFamily: UI_FONT };
const GRID = tokens.line;

// Os Y: 1500 -> „1,5 tys.”, 1350 -> „1,35 tys.”, 20 000 -> „20 tys.”
const axisFmt = (n: number) => (Math.abs(n) >= 1000 ? `${formatNumber(Math.round(n / 10) / 100)} tys.` : formatNumber(n));

interface TooltipEntry {
  name?: string | number;
  value?: number | string | (number | string)[];
  color?: string;
  fill?: string;
  stroke?: string;
  payload?: { color?: string; fill?: string };
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
  format: (value: number, name: string) => string;
  hideLabel?: boolean;
}

// Dymek w stylu motywu: najpierw wartosc, potem nazwa serii; kolor tylko na kresce obok.
const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, payload, label, format, hideLabel }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <Box
      sx={{
        bgcolor: tokens.paper,
        border: `1px solid ${tokens.line}`,
        borderRadius: '10px',
        boxShadow: 4,
        px: 1.5,
        py: 1.1,
        minWidth: 140,
        fontFamily: UI_FONT,
      }}
    >
      {!hideLabel && label !== undefined && label !== '' && (
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, mb: 0.5 }}>{label}</Typography>
      )}
      <Stack gap={0.4}>
        {payload.map((p, i) => {
          const name = String(p.name ?? '');
          const color = p.payload?.color || p.color || p.fill || p.stroke || tokens.plum;
          return (
            <Stack key={`${name}-${i}`} direction="row" alignItems="center" gap={1}>
              <Box sx={{ width: 12, height: 3, borderRadius: 2, bgcolor: color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: tokens.ink, fontVariantNumeric: 'tabular-nums' }}>
                {format(Number(p.value) || 0, name)}
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>{name}</Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

const legendFormatter = (value: string) => (
  <span style={{ color: tokens.inkSoft, fontSize: 13, fontWeight: 600, fontFamily: UI_FONT }}>{value}</span>
);

// Revenue trend (area). data: { label, revenue }[]
export const RevenueAreaChart: React.FC<{ data: { label: string; revenue: number }[]; height?: number }> = ({
  data,
  height = 260,
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: tokens.lineStrong }} minTickGap={20} tickMargin={8} />
      <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={62} tickFormatter={axisFmt} />
      <Tooltip
        cursor={{ stroke: tokens.lineStrong, strokeWidth: 1 }}
        content={<ChartTooltip format={(v) => formatMoney(v)} />}
      />
      <Area
        type="monotone"
        dataKey="revenue"
        name="Przychód"
        stroke={CHART_COLORS[0]}
        strokeWidth={2}
        fill={CHART_COLORS[0]}
        fillOpacity={0.08}
        activeDot={{ r: 5, stroke: tokens.paper, strokeWidth: 2, fill: CHART_COLORS[0] }}
      />
    </AreaChart>
  </ResponsiveContainer>
);

interface BarSpec {
  key: string;
  name: string;
  color: string;
  currency?: boolean;
}

// Generic (grouped) bar chart. data rows keyed by xKey + each bar key.
export const GroupedBarChart: React.FC<{
  data: Record<string, unknown>[];
  xKey: string;
  bars: BarSpec[];
  height?: number;
}> = ({ data, xKey, bars, height = 280 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="22%" barGap={2}>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis dataKey={xKey} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: tokens.lineStrong }} minTickGap={8} tickMargin={8} />
      <YAxis
        tick={AXIS_TICK}
        tickLine={false}
        axisLine={false}
        width={bars.some((b) => b.currency) ? 62 : 44}
        tickFormatter={axisFmt}
      />
      <Tooltip
        cursor={{ fill: alpha(tokens.plum, 0.05) }}
        content={
          <ChartTooltip
            format={(v, name) => {
              const spec = bars.find((b) => b.name === name);
              return spec?.currency ? formatMoney(v) : formatNumber(v);
            }}
          />
        }
      />
      {bars.length > 1 && (
        <Legend
          verticalAlign="top"
          align="left"
          iconType="square"
          iconSize={10}
          height={34}
          wrapperStyle={{ fontFamily: UI_FONT }}
          formatter={legendFormatter}
        />
      )}
      {bars.map((b) => (
        <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color} radius={[4, 4, 0, 0]} maxBarSize={28} />
      ))}
    </BarChart>
  </ResponsiveContainer>
);

// Donut with legend + center total. data: { name, value, color }[]
export const DonutChart: React.FC<{
  data: { name: string; value: number; color: string }[];
  currency?: boolean;
  height?: number;
}> = ({ data, currency = true, height = 240 }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  const nonEmpty = data.filter((d) => d.value > 0);
  const slices = nonEmpty.length ? nonEmpty : data;
  const size = Math.min(height, 210);
  const fmt = (v: number) => (currency ? formatMoney(Math.round(v)) : formatNumber(v));
  return (
    // Uklad zalezy od szerokosci karty, nie ekranu: w waskiej kolumnie legenda schodzi pod wykres.
    <Box sx={{ containerType: 'inline-size' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2.5,
          '@container (min-width: 440px)': { flexDirection: 'row', gap: 3.5 },
        }}
      >
        <Box sx={{ position: 'relative', width: size, maxWidth: '100%', height: size, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height={size}>
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="name"
                innerRadius="66%"
                outerRadius="100%"
                paddingAngle={slices.length > 1 ? 1 : 0}
                stroke={tokens.paper}
                strokeWidth={slices.length > 1 ? 2 : 0}
                startAngle={90}
                endAngle={-270}
              >
                {slices.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip hideLabel format={(v) => fmt(v)} />} />
            </PieChart>
          </ResponsiveContainer>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              px: 3,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 750, lineHeight: 1.2, letterSpacing: '-0.01em' }}>{fmt(total)}</Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: tokens.muted, mt: 0.25 }}>Razem</Typography>
          </Box>
        </Box>
        <Stack component="ul" gap={1.1} sx={{ flex: 1, width: '100%', minWidth: 0, listStyle: 'none', m: 0, p: 0 }}>
          {data.map((d) => (
            <Stack component="li" key={d.name} direction="row" alignItems="center" gap={1.25}>
              <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: d.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft, flex: 1, minWidth: 0 }}>{d.name}</Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {fmt(d.value)}
              </Typography>
              <Typography
                sx={{ fontSize: '0.8125rem', color: tokens.muted, width: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
              >
                {total > 0 ? Math.round((d.value / total) * 100) : 0}%
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export { CHART_COLORS };
